import useFormatter from "@/hooks/use-formatter.hook.ts";
import useGoalsHook from "@/hooks/use-goals.hook.tsx";
import { GoalCardBreadcrumbs } from "@/pages/talent/goals/components/GoalCardBreadcrumbs.tsx";
import {
  ApiGoalConfidenceLevel,
  ApiGoalShort,
  ApiGoalStatus,
  GoalConfidenceLevelItems,
} from "@/types/goals.types.ts";
import {
  Box,
  ButtonIcon,
  DotSeparator,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface GoalCardProps {
  goal: ApiGoalShort;
  clickable?: boolean;
  disableDecoration?: boolean;
  onClick?: (goal: ApiGoalShort) => void;
  onInfoClick?: (goal: ApiGoalShort) => void;
  to?: string;
  target?: React.HTMLAttributeAnchorTarget;
}

export function GoalCard({
  goal,
  clickable = true,
  disableDecoration = false,
  onClick,
  onInfoClick,
  to,
  target,
}: GoalCardProps) {
  const { t } = useTranslation();
  const { getOverdueDays, getDaysToDate, roundPercent } = useFormatter();
  const {
    renderOwnerAvatar,
    getGoalStatus,
    renderAssignToAvatar,
    getOwnerName,
    getOwnerTypeTitle,
  } = useGoalsHook();

  const confidenceLevelItems = GoalConfidenceLevelItems();

  const renderConfidenceLevelText = (level: ApiGoalConfidenceLevel) => {
    const levelItem = confidenceLevelItems.find((item) => item.id === level);
    return levelItem?.title ?? "";
  };

  const renderDeadlineStatus = (endDate: Date) => {
    const overdueDays = getOverdueDays(endDate);
    if (overdueDays > 0 && goal.status === ApiGoalStatus.active) {
      return t("goals:overdue", { days: t("common:days_short", { count: overdueDays }) });
    }
    const daysLeft = getDaysToDate(endDate);
    return t("plurals:days_left_short", { count: daysLeft });
  };

  const cardClassName = twMerge(
    "bg-dark/5 border border-dark/5 rounded-xs px-4 py-5",
    clickable && "cursor-pointer"
  );
  const ownerName = getOwnerName(goal);

  const content = (
    <Stack>
      <GoalCardBreadcrumbs goal={goal} clickable={clickable} onClick={onClick} target={target} />
      <ListItem
        behavior={"card"}
        icon={goal.assign_to ? renderAssignToAvatar(goal) : renderOwnerAvatar(goal)}
        titleSlot={
          <Stack
            className={"whitespace-nowrap text-ellipsis overflow-hidden font-semibold"}
            gap={"xs"}
            items={"center"}
            direction={"horizontal"}
          >
            {!goal.is_shared ? <FontAwesomeIcon icon={"fa-light fa-lock"} /> : undefined}

            <Text
              className={twMerge(
                goal.status !== ApiGoalStatus.active && !disableDecoration
                  ? "line-through text-secondary"
                  : ""
              )}
            >
              {goal.title}
            </Text>
          </Stack>
        }
        captionSlot={
          ownerName && (
            <Stack className={"whitespace-nowrap"} direction={"horizontal"}>
              <Tooltip>
                <TooltipTrigger>
                  <Text className="text-dark/40">@{ownerName}</Text>
                </TooltipTrigger>
                <TooltipContent>{getOwnerTypeTitle(goal.owner_type)}</TooltipContent>
              </Tooltip>
            </Stack>
          )
        }
        valueSlot={
          <Stack gap={"lg"} direction={"horizontal"} items={"center"}>
            <Stack className={"text-right"} items={"end"}>
              <Text>
                {goal.status === ApiGoalStatus.active
                  ? t("common:completed_percent", { percent: roundPercent(goal.progress) })
                  : getGoalStatus(goal).label}
              </Text>
              {goal.status === ApiGoalStatus.active && (
                <DotSeparator>
                  <Text className={"text-secondary"}>
                    {renderConfidenceLevelText(goal.confidence_level)}
                  </Text>
                  <Text className="text-secondary">{renderDeadlineStatus(goal.date_end)}</Text>
                </DotSeparator>
              )}
            </Stack>
            {onInfoClick && (
              <ButtonIcon
                variant={"secondary"}
                onClick={(event) => {
                  onInfoClick(goal);
                  event.stopPropagation();
                  event.preventDefault();
                }}
                icon={<FontAwesomeIcon icon="fa-sharp fa-light fa-circle-info" />}
              />
            )}
          </Stack>
        }
      />
    </Stack>
  );

  if (clickable && to) {
    return (
      <Link to={to} target={target} className={cardClassName}>
        {content}
      </Link>
    );
  }

  if (clickable && onClick) {
    return (
      <Box className={cardClassName} onClick={() => onClick(goal)}>
        {content}
      </Box>
    );
  }

  return <Stack className={cardClassName}>{content}</Stack>;
}
